import { Controller } from "@hotwired/stimulus"

// Makes a rate-limited form submission VISIBLE.
//
// Rack::Attack answers a throttled POST with a 429 whose body is text/plain, and
// Turbo will not render a non-HTML error body into the page. So the submission
// failed in total silence — the button appeared to do nothing at all, no message,
// no error, nothing in the UI to explain it. That is not a hypothetical: the
// renter magic-link cap is 15 posts per IP per hour, and tenants behind shared
// NAT (a household, an office, the facility's own wifi) reach it legitimately.
//
// turbo:submit-end still fires, because the fetch SUCCEEDED at the HTTP level —
// a 429 is a response, not a network failure — so the status is there to read.
//
// Progressive enhancement only. The throttle is enforced in middleware and does
// not depend on any of this; without JS the behaviour is exactly what it was.
export default class extends Controller {
  static targets = ["submit", "notice"]

  submitEnd(event) {
    const fetchResponse = event.detail?.fetchResponse
    const response = fetchResponse?.response
    const status = response?.status ?? fetchResponse?.statusCode
    if (status !== 429) return

    // Disable rather than hide: the button staying put, greyed and inert, says
    // "this is temporarily unavailable". A vanished button reads as a broken page.
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-60", "cursor-not-allowed")
    }

    if (this.hasNoticeTarget) {
      this.noticeTarget.textContent = this.message(response)
      this.noticeTarget.classList.remove("hidden")
    }
  }

  // Retry-After is seconds, and it is the throttle's whole window (3600 for the
  // magic-link cap). Rounding it into words avoids "try again in 3600 seconds".
  message(response) {
    const seconds = parseInt(response?.headers?.get("retry-after") ?? "", 10)
    let when = "in a little while"
    if (Number.isFinite(seconds) && seconds > 0) {
      if (seconds >= 3600) {
        const hours = Math.round(seconds / 3600)
        when = hours > 1 ? `in about ${hours} hours` : "in about an hour"
      } else if (seconds >= 60) {
        when = `in about ${Math.max(1, Math.round(seconds / 60))} minutes`
      } else {
        when = "in a moment"
      }
    }
    return `Too many sign-in requests from this network. Please try again ${when}, or call the office and we'll help you in.`
  }
}
